import googleMapsApi from '@api/googleMapsApi';
import locationApi from '@api/locationApi';
import AddressInput from '@components/addressInput';
import ItemEditor from '@components/itemEditor';
import OrgMultiSelect from '@components/orgMultiSelect';
import OrgUserList from '@components/orgUserList';
import useAuth from '@hooks/useAuth';
import { useTranslator } from '@i18n';
import { Group, TextInput } from '@mantine/core';
import type { Location } from '@models/location';
import type { Org } from '@models/org';
import AreaList from '@routes/profile/locations/-areaList';
import { createFileRoute } from '@tanstack/react-router';
import { LoadingBoundary, createForm } from 'cross-state/react';

export const Route = createFileRoute('/profile/locations/$id')({
  component: LocationEditor,
});

const locationForm = createForm<Location>({
  defaultValue: {
    id: '',
    name: '',
    address: {
      street: '',
      zip: '',
      city: '',
    },
    orgs: [],
    areas: [],
  },
  validations: {
    name: { required: (x) => !!x },
    orgs: { required: (x) => x.length > 0 },
  },
});

function LocationEditor() {
  const t = useTranslator();
  const { hasClaim } = useAuth();
  const { id: locationId } = Route.useParams();
  const readonly = !hasClaim('locations/write', !locationId || locationId === 'new' ? 'any' : locationId);
  const isNew = locationId === 'new';

  return (
    <ItemEditor
      domain="locations"
      api={locationApi}
      readonly={readonly}
      form={locationForm}
      name={(location) => location.name}
      footer={
        <div
          css={{
            marginTop: 'var(--mantine-spacing-xl)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--mantine-spacing-md)',
          }}
        >
          {!isNew && <AreaList locationId={locationId} />}

          <LoadingBoundary>{!isNew && <OrgUserList type="location" orgId={locationId} />}</LoadingBoundary>
        </div>
      }
      prepareDraft={async (draft) => {
        if (draft.address) {
          const { hash, ...payload } = draft.address;

          if (!hash || hash !== JSON.stringify(payload)) {
            const result = await googleMapsApi.addressToPosition(payload).get();
            payload.placeId = result?.place_id;
            payload.lat = result?.geometry.location.lat();
            payload.lng = result?.geometry.location.lng();
          }

          draft.address = payload;
        }

        return draft;
      }}
    >
      <LoadingBoundary>
        <locationForm.Field
          name="orgs"
          render={(props) => (
            <OrgMultiSelect
              {...props}
              label={t('locations.properties.orgs.label')}
              description={t('locations.properties.orgs.description')}
              withAsterisk
              filterOptions={(org: Org) =>
                readonly ? true : org.id !== 'global' && hasClaim('locations/write', org.id)
              }
              clearable
              searchable
              type="search"
              autoComplete="off"
              spellCheck={false}
              placeholder={t('common.select')}
            />
          )}
        />
      </LoadingBoundary>

      <locationForm.Field
        name="name"
        render={(props) => (
          <TextInput
            {...props}
            label={t('locations.properties.name.label')}
            withAsterisk
            autoComplete="off"
            spellCheck={false}
          />
        )}
      />

      <locationForm.Field
        name="address.street"
        render={(props, _field, form) => (
          <AddressInput
            {...props}
            onAddressChange={(address) => {
              form.getField('address').setValue(address);
            }}
            label={t('locations.properties.street.label')}
            autoComplete="street-address"
            spellCheck={false}
          />
        )}
      />

      <Group>
        <locationForm.Field
          name="address.zip"
          render={(props) => (
            <TextInput
              {...props}
              label={t('locations.properties.zip.label')}
              autoComplete="postal-code"
              spellCheck={false}
              css={{
                flex: 1,
              }}
            />
          )}
        />

        <locationForm.Field
          name="address.city"
          render={(props) => (
            <TextInput
              {...props}
              label={t('locations.properties.city.label')}
              autoComplete="address-level2"
              spellCheck={false}
              css={{
                flex: 3,
              }}
            />
          )}
        />
      </Group>
    </ItemEditor>
  );
}
